import { useEffect, useMemo, useState } from 'react';
import { partyVar, type Candidate, type District } from '../../lib/buildCandidates';
import { useCandidates } from '../../lib/useCandidates';
import { toSlug } from '../../lib/toSlug';
import type { CandidateResponseRow } from '../../db/types';
import './Candidates.css';

const formatDistrict = (d: District) => (d === 'TBC' ? 'TBC' : `D${d}`);

const DEFAULT_DISTRICT: District | null = null;

export default function Candidates() {
  const { candidates: CANDIDATES, districts: DISTRICTS, parties: PARTIES } = useCandidates();
  const [search, setSearch] = useState('');
  const [party, setParty] = useState<string | null>(null);
  const [district, setDistrict] = useState<District | null>(DEFAULT_DISTRICT);
  const [kbyvSlugs, setKbyvSlugs] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetch('/api/know-before-you-vote')
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { profiles: CandidateResponseRow[] } | null) => {
        if (!d) return;
        setKbyvSlugs(new Set(d.profiles.map((p) => p.slug)));
      })
      .catch(() => { /* best-effort — no profiles linked */ });
  }, []);

  const filtered = useMemo<Candidate[]>(() => {
    const q = search.trim().toLowerCase();
    return CANDIDATES.filter((c) => {
      if (q && !c.name.toLowerCase().includes(q)) return false;
      if (party && c.party !== party) return false;
      if (district != null && !c.districts.includes(district)) return false;
      return true;
    });
  }, [search, party, district]);

  // Group the filtered list by party so each party's candidates sit
  // under their own header. Party order follows the canonical PARTIES
  // sequence (PL → PN → ADPD → … → others alphabetical).
  const groups = useMemo(() => {
    const byParty = new Map<string, Candidate[]>();
    for (const c of filtered) {
      const list = byParty.get(c.party) ?? [];
      list.push(c);
      byParty.set(c.party, list);
    }
    return PARTIES.filter((p) => byParty.has(p)).map((p) => ({
      party: p,
      candidates: (byParty.get(p) ?? []).sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    }));
  }, [filtered]);

  const reset = () => {
    setSearch('');
    setParty(null);
    setDistrict(DEFAULT_DISTRICT);
  };

  const isFiltered =
    search !== '' || party !== null || district !== DEFAULT_DISTRICT;

  return (
    <div className="candidates">
      <div className="candidates__controls">
        <label className="candidates__search">
          <span className="visually-hidden">Search candidates</span>
          <input
            type="search"
            placeholder="Search by name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </label>

        <FilterRow label="Party">
          <Chip active={party === null} onClick={() => setParty(null)}>
            All
          </Chip>
          {PARTIES.map((p) => (
            <Chip
              key={p}
              active={party === p}
              color={partyVar(p)}
              onClick={() => setParty(party === p ? null : p)}
            >
              {p}
            </Chip>
          ))}
        </FilterRow>

        <FilterRow label="District">
          <Chip active={district === null} onClick={() => setDistrict(null)}>
            All
          </Chip>
          {DISTRICTS.map((d) => (
            <Chip
              key={String(d)}
              active={district === d}
              onClick={() => setDistrict(district === d ? null : d)}
            >
              {formatDistrict(d)}
            </Chip>
          ))}
        </FilterRow>
      </div>

      <div className="candidates__meta">
        <span className="candidates__count">
          <strong>{filtered.length}</strong>
          {' of '}
          {CANDIDATES.length}
          {' candidates'}
        </span>
        {isFiltered && (
          <button type="button" className="candidates__reset" onClick={reset}>
            Clear filters
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="candidates__empty">
          No candidates match those filters. Try clearing them.
        </p>
      ) : (
        <div className="candidates__groups">
          {groups.map((group) => (
            <section
              key={group.party}
              className="candidates__group"
              style={{ borderTopColor: partyVar(group.party) }}
            >
              <header className="candidates__group-header">
                <span
                  className="candidates__group-pill"
                  style={{ background: partyVar(group.party) }}
                >
                  {group.party}
                </span>
                <span className="candidates__group-count">
                  {group.candidates.length}
                </span>
              </header>
              <ul className="candidates__group-list" role="list">
                {group.candidates.map((c) => (
                  <CandidateChip
                    key={`${c.name}|${c.party}`}
                    candidate={c}
                    profileSlug={kbyvSlugs.has(toSlug(c.name)) ? toSlug(c.name) : undefined}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="candidates__filter-row">
      <span className="candidates__filter-label">{label}</span>
      <div className="candidates__chips">{children}</div>
    </div>
  );
}

function Chip({
  active,
  color,
  onClick,
  children,
}: {
  active: boolean;
  color?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const style: React.CSSProperties = {};
  if (active && color) {
    style.background = color;
    style.borderColor = color;
    style.color = '#fff';
  } else if (color) {
    style.color = color;
    style.borderColor = `color-mix(in srgb, ${color} 35%, transparent)`;
  }
  return (
    <button
      type="button"
      className={`candidates__chip${active ? ' is-active' : ''}`}
      onClick={onClick}
      style={style}
    >
      {children}
    </button>
  );
}

function CandidateChip({ candidate, profileSlug }: { candidate: Candidate; profileSlug?: string }) {
  const nameEl = profileSlug ? (
    <a
      href={`/know-before-you-vote/${profileSlug}`}
      className="candidate-chip__name candidate-chip__name--linked"
    >
      {candidate.name}
    </a>
  ) : (
    <span className="candidate-chip__name">{candidate.name}</span>
  );
  return (
    <li className="candidate-chip">
      {nameEl}
      <span className="candidate-chip__districts">
        {candidate.districts.map((d) => (
          <span key={String(d)} className="candidate-chip__district">
            {formatDistrict(d)}
          </span>
        ))}
      </span>
    </li>
  );
}
